import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { createPortal } from "react-dom";
import { PetDrawer } from "../../components/PetDrawer/PetDrawer";
import {
  GiSittingDog,
  GiCat,
  GiCow,
  GiGoat,
  GiRabbit,
  GiSquirrel,
  GiTortoise,
  GiHummingbird,
} from "react-icons/gi";
import config from "../../config";

const API_BASE_URL = config.API_BASE_URL 

export const getPetIcon = (type = "") => {
  switch (type.toLowerCase()) {
    case "dog":
      return <GiSittingDog className="w-6 h-6 text-gray-700" />;

    case "cat":
      return <GiCat className="w-6 h-6 text-gray-700" />;

    case "cow":
      return <GiCow className="w-6 h-6 text-gray-700" />;

    case "goat":
      return <GiGoat className="w-6 h-6 text-gray-700" />;

    case "rabbit":
      return <GiRabbit className="w-6 h-6 text-gray-700" />;

    case "squirrel":
      return <GiSquirrel className="w-6 h-6 text-gray-700" />;

    case "tortoise":
      return <GiTortoise className="w-6 h-6 text-gray-700" />;

    case "pigeon":
    case "parrot":
      return <GiHummingbird className="w-6 h-6 text-gray-700" />;

    default:
      return <span>🐾</span>;
  }
};

// Responsive changes made to improve UI on mobile and tablet
const BoardingCalendarModel = ({
  isOpen,
  closeModal,
  selectedStartDate,
  selectedEndDate,
  formatDate,
  calculateDays,
  selectedPetType,
  setSelectedPetType,
  selectedPetSize,
  setSelectedPetSize,
  selectedRoomType,
  setSelectedRoomType,
  pendingService,
  setPendingService,
  addToCart,
}) => {
  const [currentRoomIndex, setCurrentRoomIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [isAddPetDrawerOpen, setIsAddPetDrawerOpen] = useState(false);

  const [pets, setPets] = useState([]);
  const [petsLoading, setPetsLoading] = useState(false);
  const [selectedPetId, setSelectedPetId] = useState(null);
  const [selectedPetName, setSelectedPetName] = useState(null);
  const [availableBreeds, setAvailableBreeds] = useState([]);
  const [selectedBreedId, setSelectedBreedId] = useState("");

  const [availableSizes, setAvailableSizes] = useState([]); // vendor pet sizes (flps ids)

  const [sizesByPetType, setSizesByPetType] = useState({});

  const [mappedPetSizeId, setMappedPetSizeId] = useState(null); // flps_pet_size_id
  const [apiPriceLoading, setApiPriceLoading] = useState(false);
  const [apiPrice, setApiPrice] = useState(null);
  const [apiPriceFetched, setApiPriceFetched] = useState(false); // true when API returned (even 0 if valid)
  const [priceMeta, setPriceMeta] = useState(null);
  const { token: vendorToken } = useParams();

  const [roomSelectionError, setRoomSelectionError] = useState("");

  const token = localStorage.getItem("token");
  const decoded = token ? jwtDecode(token) : null;
  const client_id = decoded?.data?.customer_data?.id;
  const isUserLoggedIn = !!client_id;

  // const petTypesFromService = pendingService?.pet_type || [];
  const [vendorPetTypes, setVendorPetTypes] = useState([]);
  const [selectedPrice, setSelectedPrice] = useState(
    pendingService?.price != null ? Number(pendingService.price) : 0,
  );

  const petTypes = (pendingService?.pet_type || []).map((pet) => ({
    pet_type_id: pet.id, // ✅ use master pet type id
    name: pet.name,
    size_label: pet.flps_pet_size_id,
  }));

  const [petSizes, setPetSizes] = useState([]);
  const [petBreeds, setPetBreeds] = useState([]);

  const fetchedPetTypesRef = useRef(new Set());

  useEffect(() => {
    const fetchAllSizes = async () => {
      try {
        // Remove the pet_type filter to get all possible sizes
        const res = await axios.get(
          `${API_BASE_URL}/customer/customerpetsize`,
        );
        // Adjust based on whether this "all" API returns .data or .data.data
        setPetSizes(res.data.data || res.data || []);
      } catch (err) {
        console.error("Size fetch error:", err);
      }
    };
    fetchAllSizes();
  }, []);

  const normalizePetTypeId = (maybe) => {
    if (!maybe) return null;
    if (typeof maybe === "string") return maybe;
    if (typeof maybe === "object") {
      return (
        maybe.pet_type_id || maybe.id || maybe._id || maybe.petTypeId || null
      );
    }
    return null;
  };

  const normalizeSizeLabel = (maybe) => {
    if (maybe == null) return "";
    if (typeof maybe === "string") return maybe.trim();
    if (typeof maybe === "object") {
      return (maybe.size_label || maybe.size || maybe.label || maybe.name || "")
        .toString()
        .trim();
    }
    return String(maybe).trim();
  };

  const getSizeLabel = (id) => {
    if (!id) return "-";
    if (!petSizes || petSizes.length === 0) return "";

    // Use the key found in your console: flps_pet_size_id
    const foundSize = petSizes.find(
      (s) =>
        String(s.flps_pet_size_id) === String(id) ||
        String(s._id) === String(id),
    );

    if (!foundSize) return "";

    return foundSize.size_label || "";
  };

  const getPetTypeNameById = (petTypeId) => {
    // Look in the master vendor list, not just the service list
    const found = vendorPetTypes.find(
      (pt) => String(pt.pet_type_id || pt.id || pt._id) === String(petTypeId),
    );

    if (found) {
      return found.name.charAt(0).toUpperCase() + found.name.slice(1);
    }

    // Fallback: If pet has a name already stored
    return "Pet";
  };

  // Get Breed Name (from pet object)
  const getBreedName = (breedIdOrObject) => {
    // 1. Extract the ID
    const id =
      typeof breedIdOrObject === "object"
        ? breedIdOrObject?._id
        : breedIdOrObject;

    if (!id) return null;
    // 3. Find with strict string conversion
    const found = petBreeds.find(
      (b) => String(b._id) === String(id) || String(b.id) === String(id),
    );
    // 4. If found, return the name. IF NOT FOUND, return null (don't return the ID!)
    return found ? found.breed_name : null;
  };

  const allowedPetTypeIds = useMemo(() => {
    if (!pendingService?.pet_type) return [];
    return pendingService.pet_type.map((pt) => pt.id);
  }, [pendingService]);

   // ✅ Display-only filter: room types this SERVICE supports for the
// currently selected pet type. Does NOT touch pricing.
  const allowedRoomTypeIds = useMemo(() => {
    const opts = pendingService?.room_type_surcharges?.options;
    if (!Array.isArray(opts) || opts.length === 0) return null; // null = "no filter"

    // Resolve the currently selected master pet-type id
    let petTypeId = null;
    if (isUserLoggedIn && selectedPetId) {
      petTypeId = pets.find((p) => p._id === selectedPetId)?.pet_type || null;
    } else if (selectedPetType) {
      petTypeId =
        selectedPetType?.pet_type_id ||
        selectedPetType?.id ||
        selectedPetType?._id ||
        selectedPetType;
    }

    const ids = new Set();
    for (const opt of opts) {
      // If the option scopes pet types and our pet type isn't in it → skip
      if (
        petTypeId &&
        Array.isArray(opt.pet_type) &&
        opt.pet_type.length > 0 &&
        !opt.pet_type.map(String).includes(String(petTypeId))
      ) {
        continue;
      }
      if (opt.room_type_id) ids.add(String(opt.room_type_id));
    }
    return ids;
  }, [
    pendingService,
    isUserLoggedIn,
    selectedPetId,
    pets,
    selectedPetType,
  ]);

  const isPetAllowedForService = (pet) => {
    if (!pet?.pet_type) return false;
    return allowedPetTypeIds.includes(pet.pet_type);
  };

  useEffect(() => {
    const loadBreeds = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/customer/getPetBreeds/`,
        );
        // VITAL: Check if your API returns { status: true, data: [...] }
        if (res.data?.status || res.data?.success) {
          setPetBreeds(res.data.data || []);
        } else {
          // Fallback for different API structures
          setPetBreeds(Array.isArray(res.data) ? res.data : []);
        }
      } catch (err) {
        console.error("Breed load error:", err);
      }
    };
    if (isOpen) loadBreeds();
  }, [isOpen]);

  useEffect(() => {
    if (!pendingService?.vendor_id) return;
    const fetchVendorPetMeta = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(
          `${API_BASE_URL}/customer/customerpettype?vendor_id=${pendingService.vendor_id}`,
          { headers },
        );
        if (res.data?.status) setVendorPetTypes(res.data.data || []);
        else setVendorPetTypes([]);
      } catch (err) {
        console.error("fetchVendorPetMeta failed:", err?.response?.data || err);
        setVendorPetTypes([]);
      }
    };
    fetchVendorPetMeta();
  }, [pendingService?.vendor_id]);

  // returns and sets availableSizes
  const fetchPetSizes = useCallback(
    async (petTypeIdRaw) => {
      const petTypeId = normalizePetTypeId(petTypeIdRaw);
      const vendorId = pendingService?.vendor_id || vendorToken;

      if (!petTypeId || !vendorId) {
        setAvailableSizes([]);
        return [];
      }

      // Check cache first
      if (fetchedPetTypesRef.current.has(petTypeId)) {
        return sizesByPetType[petTypeId] || [];
      }
      fetchedPetTypesRef.current.add(petTypeId);

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(
          `${API_BASE_URL}/customer/getPetsizes/`,
          {
            params: { vendor_id: vendorId, pet_type: petTypeId },
            headers,
          },
        );

        const sizes = res.data?.status ? res.data.data || [] : [];
        setSizesByPetType((prev) => ({ ...prev, [petTypeId]: sizes }));
        setAvailableSizes(sizes);
        return sizes;
      } catch (err) {
        console.error("fetchPetSizes error:", err);
        setSizesByPetType((prev) => ({ ...prev, [petTypeId]: [] }));
        setAvailableSizes([]);
        return [];
      }
    },
    [pendingService?.vendor_id, vendorToken, token, sizesByPetType],
  );

  useEffect(() => {
    let active = true;
    const mapSize = async () => {
      setMappedPetSizeId(null);

      const vendorId = pendingService?.vendor_id || vendorToken;
      if (!vendorId) return;

      // detect petTypeId and sizeLabel in multiple shapes
      let petTypeRaw = null;
      let sizeRaw = null;

      if (isUserLoggedIn && selectedPetId) {
        const pet = pets.find((p) => p._id === selectedPetId);
        petTypeRaw = pet?.pet_type;
        sizeRaw = pet?.size;
      } else {
        petTypeRaw =
          selectedPetType?.pet_type_id ||
          selectedPetType ||
          pendingService?.pet_type ||
          null;
        sizeRaw = selectedPetSize;
      }

      const petTypeId = normalizePetTypeId(petTypeRaw);
      const sizeLabel = normalizeSizeLabel(sizeRaw);

      if (!petTypeId || !sizeLabel) {
        // not ready yet
        return;
      }

      const sizes = await fetchPetSizes(petTypeId);
      if (!active) return;

      const savedSizeLabel = (sizeLabel || "").toLowerCase().trim();
      const matched = (sizes || []).find(
        (s) =>
          (s.size_label || "").toString().toLowerCase().trim() ===
          savedSizeLabel,
      );

      if (matched) {
        setMappedPetSizeId(matched.flps_pet_size_id);
      } else {
        setMappedPetSizeId(null);
        console.warn(
          "No matching vendor size for:",
          sizeLabel,
          "vendor sizes sample:",
          (sizes || []).slice(0, 7).map((s) => s.size_label),
        );
      }
    };

    mapSize();
    return () => {
      active = false;
    };
  }, [
    selectedPetId,
    selectedPetSize,
    selectedPetType,
    pets,
    pendingService,
    vendorToken,
    isUserLoggedIn,
  ]);

  useEffect(() => {
    if (!isUserLoggedIn || pets.length === 0) return;

    // Get unique pet_type ids from all user's pets
    const uniquePetTypes = [
      ...new Set(
        pets
          .map((p) => p.pet_type)
          .filter((id) => id && typeof id === "string" && id.length > 10), // valid ObjectId check
      ),
    ];

    if (uniquePetTypes.length === 0) {
      console.warn("Boarding: No valid pet_type ids found in user's pets");
      return;
    }

    // Fetch sizes for each pet type
    uniquePetTypes.forEach((petTypeId) => {
      fetchPetSizes(petTypeId);
    });
  }, [isUserLoggedIn, pets, pendingService?.vendor_id, vendorToken]);

  // Auto-select pet type for Guest users if only one type is available
  useEffect(() => {
    if (!isUserLoggedIn && pendingService?.pet_type?.length === 1) {
      const singleType = pendingService.pet_type[0];
      const typeId = singleType.id || singleType.pet_type_id || singleType._id;

      // Only update if not already set to avoid infinite loops
      if (selectedPetType !== typeId) {
        setSelectedPetType(typeId);
      }
    }
  }, [pendingService, isUserLoggedIn, selectedPetType, setSelectedPetType]);

  // Ensure vendor sizes are fetched for allowed pet types
  useEffect(() => {
    if (
      !isUserLoggedIn ||
      !allowedPetTypeIds.length ||
      !pendingService?.vendor_id
    ) {
      return;
    }

    // Fetch sizes for each allowed pet type
    allowedPetTypeIds.forEach((petTypeId) => {
      // Only fetch if we haven't loaded sizes for this pet type yet
      if (
        !sizesByPetType[petTypeId] ||
        sizesByPetType[petTypeId].length === 0
      ) {
        console.log(`Fetching vendor sizes for pet type: ${petTypeId}`);
        fetchPetSizes(petTypeId);
      }
    });
  }, [
    allowedPetTypeIds,
    pendingService?.vendor_id,
    isUserLoggedIn,
    sizesByPetType,
  ]);

  useEffect(() => {
    const typeId = normalizePetTypeId(selectedPetType);
    if (!isUserLoggedIn && typeId) {
      console.log("Guest mode: Pet type detected, fetching sizes...", typeId);
      fetchPetSizes(typeId);
    }
  }, [selectedPetType, isUserLoggedIn, pendingService?.vendor_id]);

  const fetchBreeds = async (petTypeId) => {
    if (!petTypeId || !pendingService?.vendor_id) {
      setAvailableBreeds([]);
      return [];
    }

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.get(
        `${API_BASE_URL}/customer/getPetBreeds/`,
        {
          params: { pet_type: petTypeId },
          headers,
        },
      );

      console.log("getPetBreeds response:", res?.data);
      const breeds = res.data?.status ? res.data.data || [] : [];
      setAvailableBreeds(breeds);
      return breeds;
    } catch (err) {
      console.error("fetchBreeds failed:", {
        message: err.message,
        status: err.response?.status,
        response: err.response?.data,
      });
      setAvailableBreeds([]);
      return [];
    }
  };

  const getPetSizeFromSelectedPet = () => {
    if (!isUserLoggedIn || !selectedPetId) return null;
    const pet = pets.find((p) => p._id === selectedPetId);
    return pet?.size || null;
  };
  useEffect(() => {
    if (selectedPetId) {
      console.log("Selected pet size (ID):", getPetSizeFromSelectedPet());
    }
  }, [selectedPetId]);

  useEffect(() => {
    if (isOpen && vendorToken) {
      // Fetch breeds for both Dog and Cat (or iterate through petTypes)

      const loadBreeds = async () => {
        try {
          const res = await axios.get(
            `${API_BASE_URL}/customer/getPetBreeds/`,
          );
          if (res.data?.status) {
            setPetBreeds(res.data.data);
          }
        } catch (err) {
          console.error("Failed to load master breed list", err);
        }
      };
      loadBreeds();
    }
  }, [isOpen, vendorToken]);

  const buildBoardingPricePayload = ({ includeRoom = false } = {}) => {
    if (!pendingService) return null;

    const vendorId = pendingService.vendor_id || vendorToken;
    if (!vendorId || !pendingService.service_id) return null;
    if (!selectedStartDate || !selectedEndDate) return null;

    const petSizeValue =
      mappedPetSizeId ||
      selectedPetSize?.flps_pet_size_id ||
      getPetSizeFromSelectedPet();

    if (!petSizeValue) {
      console.warn("Blocked price call: pet_size missing");
      return null;
    }

    const payload = {
      vendor_id: vendorId,
      vendor_name:pendingService.vendor_name,
      service_type: "boarding",
      service_id: pendingService.service_id,
      pet_size: petSizeValue,
      checkin_date: formatLocalISO(selectedStartDate),
      checkout_date: formatLocalISO(selectedEndDate),
      ...(selectedPetId ? { pet_id: selectedPetId } : {}),
    };

    // 🟢 room only when selected
    if (includeRoom && selectedRoom?.roomTypeId) {
      payload.room_type_id = selectedRoom.roomTypeId;
    }

    return payload;
  };

  const formatLocalISO = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  // 🔹 Fetch Pets for logged-in user

  const fetchPets = async () => {
    if (!isUserLoggedIn || !client_id || !token) return;
    setPetsLoading(true);
    try {
      const sizeRes = await axios.get(
        `${API_BASE_URL}/customer/customerpetsize`,
      );
      const petSizes = sizeRes.data || [];

      // 2. Define the helper locally so 'map' can see it
      const getLabel = (sizeId) => {
        const found = petSizes.find(
          (s) =>
            String(s.flps_pet_size_id) === String(sizeId) ||
            String(s._id) === String(sizeId),
        );
        return found ? found.size_label : "Unknown";
      };

      const res = await axios.post(
        `${API_BASE_URL}/customer/getallpet`,
        {
          client_id,
          serviceId: pendingService?.service_id, // Pass service ID to get size_price
          serviceType: "boarding",
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const petData = (res.data?.pets || []).map((p) => ({
        ...p,
        _id: p._id || p.id,
        // current_size_price: p.size_price ?? 0,
        // sizeLabel: getLabel(p.size)
      }));
      console.log("pet data", res.data?.pets);
      setPets(petData);
    } catch (error) {
      console.error("Error fetching pets:", error);
      setPets([]);
    }finally {
      setPetsLoading(false); // ← critical
  }
  };
  // fetchPets();
  //}, [isUserLoggedIn, client_id, token]);

  useEffect(() => {
    if (!isUserLoggedIn || !client_id || !token) return;
    fetchPets();
  }, [isUserLoggedIn, client_id, token]);

  // 🔹 Keyboard Navigation
  useEffect(() => {
    // Only activate if the modal is open and rooms are loaded
    if (!isOpen || rooms.length === 0) return;

    const handleKeydown = (event) => {
      // Stop default browser actions (like page scrolling or focus change)
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        handlePrevRoom();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        handleNextRoom();
      }
    };

    // Attach the event listener when the modal is open
    document.addEventListener("keydown", handleKeydown);

    // Clean up when the component unmounts or modal closes
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, [isOpen, rooms.length]);

  // 🔹 Map room API data
  const mapRoomData = (rawData) =>
    rawData.map((room, index) => ({
      roomTypeId: room._id,
      name: room.room_name,
      //price: room.room_price ?? 0,
       price: Number(room.room_price?.roomSurcharge ?? room.room_price ?? 0) || 0,
      // price now linked dynamically below in UI using pendingService.price_by_room_type
      // image: room.images?.[0] || "",
      images: room.images ?? [],
      index, // store index to match with price_by_room_type
      details: {
        "Pet Type": room.pet_type,
        "Kennel Size": room.kennel_size,
        "Max Occupancy": room.occupancy_limit,
        Environment: room.environment_type,
        Category: room.room_category,
        Camera: room.camera,
        Description: room.description || "No description provided.",
      },
    }));

  const getSelectedPetTypeName = () => {
    // 🟢 LOGGED-IN USER
    if (isUserLoggedIn && selectedPetId) {
      const pet = pets.find((p) => p._id === selectedPetId);
      if (!pet) return null;

      const foundType = petTypes.find(
        (pt) => String(pt.pet_type_id) === String(pet.pet_type),
      );

      // Capitalize first letter → "dog" → "Dog"
      return foundType?.name
        ? foundType.name.charAt(0).toUpperCase() + foundType.name.slice(1)
        : null;
    }

    // 🟢 GUEST USER
    if (selectedPetType) {
      const foundType = petTypes.find(
        (pt) => String(pt.pet_type_id) === String(selectedPetType),
      );

      return foundType?.name
        ? foundType.name.charAt(0).toUpperCase() + foundType.name.slice(1)
        : null;
    }

    return null;
  };

  // 🔹 Fetch Rooms
  useEffect(() => {
    if (!isOpen || !vendorToken) return;

    console.log("ROOM EFFECT HIT", {
      isOpen,
      vendorToken,
      selectedPetId,
      selectedPetType,
      pets,
      petTypes,
    });
    const petTypeName = getSelectedPetTypeName();
    if (!petTypeName) return; // ⛔ don’t call API without petType

    setCurrentRoomIndex(0);
    setSelectedRoom(null);

    const fetchRooms = async () => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/room/getAvailablerooms/${vendorToken}`,
          {
            params: {
              petType: petTypeName, // e.g., "Dog" or "Cat"
              serviceId: pendingService?.service_id, // Ensure this key matches backend (serviceId vs service_id)
            },
          },
        );

        if (res.data?.status && res.data?.data) {
          //setRooms(mapRoomData(res.data.data));
          const rawRooms = res.data.data;
          const visibleRooms =
            allowedRoomTypeIds === null
              ? rawRooms
              : rawRooms.filter((r) => allowedRoomTypeIds.has(String(r._id)));

          setRooms(mapRoomData(visibleRooms));    // ✅ same mapRoomData, same pricing
        } else {
          setRooms([]);
          toast.error(res.data?.message || "No rooms available");
        }
      } catch (error) {
        console.error("API fetch error:", error);
        toast.error("Something went wrong while fetching rooms.");
        setRooms([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRooms();
  }, [
    isOpen,
    vendorToken,
    selectedPetId, // logged-in user
    selectedPetType, // guest user
    allowedRoomTypeIds,
  ]);

  // 🔹 Base Price Only (Dates-based)
  let basePrice = Number(pendingService.basic_price ?? 0);
  const getTotalPrice = () => {
    if (!pendingService) return 0;
    if (!selectedStartDate || !selectedEndDate) return 0;

    const days = calculateDays ? calculateDays() : 1;

    return basePrice * days;
  };
  const totalPrice = useMemo(
    () => getTotalPrice(),
    [
      pendingService,
      selectedStartDate,
      selectedEndDate,
      selectedPetId,
      selectedRoom,
    ],
  );

  useEffect(() => {
    if (!selectedRoom) {
      setApiPrice(null);
      setApiPriceFetched(false);
      setPriceMeta(null);
    }
  }, [selectedRoom]);

  const fetchBoardingPrice = async ({ includeRoom = false } = {}) => {
    const payload = buildBoardingPricePayload({ includeRoom });

    if (!payload) return;

    try {
      setApiPriceLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.post(
        `${API_BASE_URL}/customer/getServicePriceByPet`,
        payload,
        { headers },
      );

      if (res.data?.status || res.data?.success) {
        const price = res.data?.data?.price ?? res.data?.data ?? null;

        setApiPrice(price != null ? Number(price) : null);
        setApiPriceFetched(true);
        const apiData = res.data?.data || {};
        const days = calculateDays ? calculateDays() : 1;
        const basePricePerDay = Number(pendingService?.basic_price ?? 0);

        setPriceMeta({
          base_price_per_day: basePricePerDay,
          days,
          surchargeDetails: apiData.surchargeDetails || [],
          taxAmount: apiData.taxAmount || 0,
          final_price: Number(apiData.price ?? 0),
        });
        //setPriceMeta(res.data?.data || null);
        setSelectedPrice(price != null ? Number(price) : 0);
      }
    } catch (err) {
      console.error("Price fetch error:", err);
    } finally {
      setApiPriceLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedRoom) return;
    if (!selectedStartDate || !selectedEndDate) return;

    // pet must already be selected
    if (!mappedPetSizeId && !selectedPetId && !selectedPetSize) return;

    const t = setTimeout(() => {
      fetchBoardingPrice({ includeRoom: true });
    }, 300);

    return () => clearTimeout(t);
  }, [selectedRoom?.roomTypeId]);

  useEffect(() => {
    if (!isUserLoggedIn) return;
    if (!selectedPetId) return;
    if (!selectedStartDate || !selectedEndDate) return;

    const t = setTimeout(() => {
      fetchBoardingPrice({ includeRoom: false });
    }, 300);

    return () => clearTimeout(t);
  }, [selectedPetId, mappedPetSizeId, selectedStartDate, selectedEndDate]);

  //  PRICE FETCH WHEN GUEST PET DETAILS ARE SELECTED (1st stage)
  useEffect(() => {
    if (isUserLoggedIn) return;
    if (!selectedPetType || !selectedPetSize) return;
    if (!selectedStartDate || !selectedEndDate) return;

    const t = setTimeout(() => {
      fetchBoardingPrice({ includeRoom: false });
    }, 300);

    return () => clearTimeout(t);
  }, [
    selectedPetType,
    selectedPetSize,
    mappedPetSizeId,
    selectedStartDate,
    selectedEndDate,
  ]);

  useEffect(() => {
    if (apiPriceFetched) {
      // 1. If API price exists (which includes pet surcharge), use it.
      setSelectedPrice(apiPrice != null ? Number(apiPrice) : 0);
    } else if (selectedPetId || (selectedPetType && selectedPetSize)) {
      // 2. If API hasn't returned yet but pet is selected,
      // we should wait or use a local calc that includes size_price.
      setSelectedPrice(totalPrice);
    } else {
      // 3. Fallback to basic price only if no pet is selected
      setSelectedPrice(typeof totalPrice === "number" ? totalPrice : 0);
    }
  }, [apiPrice, apiPriceFetched, totalPrice, selectedPetId, selectedRoom]);
  // 🔹 Scroll Lock Management
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.dataset.scrollY = scrollY.toString();

      // ⭐️ Apply the class instead of setting styles individually ⭐️
      document.body.classList.add("no-scroll");
      document.body.style.top = `-${scrollY}px`;

      // ... (Touchmove Listener remains) ...
      const preventTouchMove = (e) => {
        // Allows scrolling inside modal content (adjust class names if needed)
        if (
          e.target.closest(".overflow-y-auto") ||
          e.target.closest(".room-details-scroll")
        )
          return;
        e.preventDefault();
      };

      document.addEventListener("touchmove", preventTouchMove, {
        passive: false,
      });

      return () => {
        document.removeEventListener("touchmove", preventTouchMove);

        // ⭐️ Remove the class ⭐️
        document.body.classList.remove("no-scroll");
        document.body.style.top = "";

        const restoredScrollY = document.body.dataset.scrollY;
        if (restoredScrollY) {
          setTimeout(() => {
            window.scrollTo(0, parseInt(restoredScrollY, 10));
            delete document.body.dataset.scrollY;
          }, 0);
        }
      };
    }
    return () => {};
  }, [isOpen]);

  if (!isOpen) return null;

  // 🔹 Carousel logic
  const handlePrevRoom = () => {
    if (isAnimating || rooms.length === 0) return;
    setIsAnimating(true);
    //setCurrentRoomIndex((prev) => (prev === 0 ? rooms.length - 1 : prev - 1));
    setCurrentRoomIndex((prev) => (prev === 0 ? 0 : prev - 1));
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleNextRoom = () => {
    if (isAnimating || rooms.length === 0) return;
    setIsAnimating(true);
    const maxIndex = Math.max(0, rooms.length - 1);

    setCurrentRoomIndex((prev) => (prev >= maxIndex ? maxIndex : prev + 1));
    //setCurrentRoomIndex((prev) => (prev === rooms.length - 1 ? 0 : prev + 1));
    setTimeout(() => setIsAnimating(false), 300);
  };

  const getStepPercentage = () => {
    // Note: window.innerWidth reflects the current viewport width
    if (window.innerWidth >= 1024) {
      return 50;
    } else if (window.innerWidth >= 640) {
      return 33.3333;
    } else {
      return 50;
    }
  };

  // 🔹 Booking confirmation
  const handleConfirmBooking = async () => {
    if (!selectedStartDate || !selectedEndDate)
      return toast.error("Please select check-in and check-out dates");
    if (!selectedRoom) return toast.error("Please select a room");
    if (isUserLoggedIn && !selectedPetId)
      return toast.error("Please select your pet");
    if (!isUserLoggedIn && (!selectedPetType || !selectedPetSize))
      return toast.error("Please select pet type and size");

    const days = calculateDays ? calculateDays() : 1;
    //const toISODate = (date) => date.toISOString().split("T")[0];

    const toLocalISODate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    const priceMetaToSend = priceMeta || {
      base_price_per_day: basePrice,
      days,
      surchargeDetails: [], // can be improved later
      final_price: displayedPrice,
    };

    const bookingDetails = {
      ...pendingService,
      item_type: "boarding",
      start_date: toLocalISODate(selectedStartDate),
      end_date: toLocalISODate(selectedEndDate),
      duration: days,
      pet_type: selectedPetType || pendingService?.pet_type,
      price: displayedPrice,
      //unit_price: displayedPrice / days,
      unit_price: displayedPrice,
      total_price: displayedPrice,
      priceMeta: priceMetaToSend,
      ...(isUserLoggedIn
        ? { vendor_pet_id: selectedPetId, petName: selectedPetName }
        : {
            petType: selectedPetType,
            pet_type_name: getSelectedPetTypeName(),
            petSize: selectedPetSize,
          }),
      room_type_id: selectedRoom?.roomTypeId,
      room_name: selectedRoom?.name,

      allowedPetTypes: pendingService.pet_type || [],
    };
    console.log("DEBUG: Sending to Cart ->", {
      totalPrice: displayedPrice,
      unitPrice: displayedPrice / days,
      days: days,
    });
    //console.log("Saving to cart → allowedPetTypes:", pendingService.pet_type);
    console.log("Saving to cart with price_meta:", priceMetaToSend); // ← debug this!

    console.log("bookingDetails before addToCart:", bookingDetails);
    console.log("price_meta type:", typeof bookingDetails.price_meta);
    console.log("price_meta content:", bookingDetails.price_meta);

    await Promise.resolve(addToCart(bookingDetails));
    document.body.classList.remove("no-scroll");
    closeModal();
    if (typeof setPendingService === "function") {
      setPendingService(null);
    }
  };

  // 🔹 Render visible rooms
  const getRoomsToDisplay = () => {
    if (rooms.length === 0) return [];
    const displayCount = Math.min(3, rooms.length);
    return Array.from(
      { length: displayCount },
      (_, i) => rooms[(currentRoomIndex + i) % rooms.length],
    );
  };
  const roomsToDisplay = getRoomsToDisplay();

  //const displayedPrice = apiPrice != null ? apiPrice : totalPrice;
  const displayedPrice =
    selectedPetId || (selectedPetType && selectedPetSize)
      ? (apiPrice ?? totalPrice)
      : totalPrice;

  const renderPetSection = () => {
    if (isUserLoggedIn) {
      console.log("=== DEBUG PET SECTION ===");
      console.log("Total pets:", pets.length);
      console.log("Allowed pet type IDs:", allowedPetTypeIds);
      console.log("Sizes by pet type (empty arrays):", sizesByPetType);

      if (petsLoading) {
          return (
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                🐾 <span className="text-orange-600">Select Your Pet</span>
              </h3>
              <div className="text-center py-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FD5901] mx-auto"></div>
                <p className="mt-4 text-gray-600 font-medium">Loading your pets...</p>
              </div>
            </div>
          );
        }

      // 1. FIRST: Filter only by pet type (ignore size for now)
      const allowedByTypePets = pets.filter((pet) => {
        return allowedPetTypeIds.includes(pet.pet_type);
      });

      console.log("Pets allowed by type:", allowedByTypePets.length);

      if (allowedByTypePets.length === 0) {
        return (
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              🐾 <span className="text-orange-600">Select Your Pet</span>
            </h3>
            <div className="text-center py-8 bg-gray-50 rounded-2xl border border-gray-200">
              <p className="text-gray-600 mb-4">
                {pets.length === 0
                  ? "No pets found. Add a pet to continue."
                  : "No pets match this service's pet type requirements."}
              </p>
              <button
                onClick={() => setIsAddPetDrawerOpen(true)}
                className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-orange-700"
              >
                <span className="text-xl">+</span> Add a Pet
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            🐾 <span className="text-orange-600">Select Your Pet</span>
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {allowedByTypePets.map((pet) => {
              const isSelected = selectedPetId === pet._id;
              const vendorSizes = sizesByPetType[pet.pet_type] || [];
              const sizesLoaded = vendorSizes.length > 0;

              // Check if pet size is compatible (only if sizes are loaded)
              let isSizeCompatible = true;
              let sizeStatus = "";

              if (sizesLoaded) {
                const foundSize = vendorSizes.find((vendorSize) => {
                  return (
                    String(vendorSize.flps_pet_size_id) === String(pet.size) ||
                    String(vendorSize._id) === String(pet.size) ||
                    (vendorSize.size_label || "").toLowerCase().trim() ===
                      (getSizeLabel(pet.size) || "").toLowerCase().trim()
                  );
                });

                isSizeCompatible = !!foundSize;
                sizeStatus = isSizeCompatible
                  ? "Available"
                  : "Size not supported";
              } else {
                sizeStatus = "Checking availability...";
              }

              return (
                <button
                  key={pet._id}
                  onClick={() => {
                    // Allow selection even while checking sizes
                    // Only show warning if sizes are loaded AND incompatible
                    if (sizesLoaded && !isSizeCompatible) {
                      toast.error(
                        "This pet size is not available for this service",
                      );
                      return;
                    }

                    // If still checking sizes, show info but allow selection
                    if (!sizesLoaded) {
                      toast.info(
                        "Checking size compatibility... You can proceed for now.",
                      );
                    }

                    if (selectedPetId === pet._id) {
                      setSelectedPetId(null);
                      setSelectedPetName(null);
                      setMappedPetSizeId(null);
                      setRooms([]);
                      setSelectedRoom(null);
                      setApiPrice(null);
                      setApiPriceFetched(false);
                    } else {
                      setSelectedPetId(pet._id);
                      setSelectedPetName(pet.name);
                      setMappedPetSizeId(pet.size);
                    }
                  }}
                  className={`group relative rounded-xl overflow-hidden border-2 transition-all duration-300 hover:shadow-md 
                  ${
                    !sizesLoaded && !isSelected
                      ? "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      : sizesLoaded && !isSizeCompatible
                        ? "bg-red-50 border-red-200 cursor-not-allowed"
                        : isSelected
                          ? "bg-orange-500 text-white border-orange-500 shadow-md"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-orange-100 hover:border-orange-300"
                  }`}
                  aria-pressed={isSelected}
                  // Only disable if sizes are loaded AND incompatible
                  disabled={sizesLoaded && !isSizeCompatible}
                >
                  <div className="px-4 py-1 text-left">
                    <div className="flex justify-between items-center">
                      <p
                        className={`font-semibold text-base capitalize ${
                          isSelected ? "text-gray-900" : "text-gray-900"
                        }`}
                      >
                        {pet.name}
                      </p>
                      {isSelected && (
                        <div className="bg-orange-100 text-orange-500 rounded-full w-5 h-5 flex items-center justify-center shadow-sm">
                          <span className="text-sm font-bold">✓</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-1">
                      <div className="flex flex-wrap items-center gap-3">
                        <span
                          className={`px-1 py-0.5 rounded text-[12px] font-bold uppercase tracking-wider ${
                            isSelected
                              ? "bg-gray-100 text-gray-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {getPetTypeNameById(pet.pet_type)}
                        </span>
                        <span
                          className={`text-sm ${
                            isSelected ? "text-blue-100" : "text-blue-500"
                          }`}
                        >
                          {getSizeLabel ? getSizeLabel(pet.size) : ""}
                        </span>
                      </div>

                      {/* Size status message */}
                      {sizeStatus && (
                        <div className="mt-1">
                          <span
                            className={`text-xs font-medium ${
                              !sizesLoaded
                                ? "text-gray-500 italic"
                                : !isSizeCompatible
                                  ? "text-red-500"
                                  : "text-green-500"
                            }`}
                          >
                            {sizeStatus}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Add Pet Button */}
            <button
              onClick={() => setIsAddPetDrawerOpen(true)}
              className="rounded-xl border-2 border-dashed border-orange-400 bg-orange-50/60 hover:bg-orange-100 hover:border-orange-500 transition-all duration-300 flex flex-col items-center justify-center gap-1.5 py-1 group"
            >
              <div className="bg-orange-200 text-orange-600 rounded-full w-8 h-8 flex items-center justify-center text-xl font-light group-hover:bg-orange-300 transition-colors">
                +
              </div>
              <span className="text-xs font-medium text-orange-700">
                Add Pet
              </span>
            </button>
          </div>
        </div>
      );
    }

    // Guest flow (keep your existing guest flow code - it starts here)

    // Guest flow
    return (
      <>
        <div className="px-2">
          <h3 className="text-gray-800 font-semibold text-lg mb-2 px-4 flex items-center gap-2">
            🐾 <span className="text-orange-600">Pet Type</span>
               <span className="text-red-500">*</span>
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-2 ">
            {petTypes && petTypes.length > 0 ? (
              petTypes.map((pet) => {
                const petId = pet.pet_type_id || pet.id || pet._id;
                const isSelected = selectedPetType === petId;
                return (
                  <label
                    key={petId}
                    onClick={async () => {
                      // set pet type id
                      setSelectedPetType(petId);

                      // clear dependent state
                      setSelectedPetSize(null);
                      setSelectedBreedId("");
                      setAvailableSizes([]);
                      setAvailableBreeds([]);

                      // fetch sizes & breeds for this pet type
                      //await fetchPetSizes(petId);
                      //await fetchBreeds(petId);
                    //}}
                    if (sizesByPetType[petId] && sizesByPetType[petId].length > 0) {
                        // Use cached sizes immediately
                        setAvailableSizes(sizesByPetType[petId]);
                      } else {
                        // Only fetch if not in cache
                        setAvailableSizes([]); // Clear while fetching
                        await fetchPetSizes(petId);
                      }                      
                      await fetchBreeds(petId);
                    }}
                    className={`flex items-center gap-6 p-2 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                      isSelected
                        ? "border-[#FD5901] bg-orange-50 shadow-md"
                        : "border-gray-200 hover:shadow-sm bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="petType"
                      value={petId}
                      checked={isSelected}
                      onChange={() => setSelectedPetType(petId)}
                      className="hidden"
                    />

                    <span className="text-xl ml-6">
                       {getPetIcon(pet.name)}
                    </span>

                    <span className="font-semibold capitalize text-gray-700">
                      {pet.name}
                    </span>
                  </label>
                );
              })
            ) : (
              <div className="px-4">
                <p className="text-sm text-gray-500">
                  Pet types are not available for this service.
                </p>
              </div>
            )}
          </div>

          {/* Pet Size */}
          <div>
            <h3 className="text-gray-800 font-semibold text-lg mb-3 px-2 mt-2 flex items-center gap-2">
              📏 <span className="text-orange-600">Pet Size</span>
                 <span className="text-red-500">*</span>
            </h3>
            <div className="flex flex-wrap gap-2">
              {["Small", "Medium", "Large", "Giant"].map((label) => {
                const sizeData = availableSizes?.find(
                  (s) => s.size_label?.toLowerCase() === label.toLowerCase(),
                );
                const isSupported = !!sizeData;
                const isSelected = selectedPetSize?.size_label === label;

                return (
                  <div key={label} className="relative group">
                    <button
                      disabled={!isSupported || !selectedPetType}
                      onClick={() => {
                        // Reset room first
                        if (selectedRoom) {
                          setSelectedRoom(null);
                        }
                        setSelectedPetSize(sizeData);
                      }}
                      className={`py-1.5 px-4 rounded-lg border text-sm font-medium transition-all ${
                        !selectedPetType
                          ? "bg-gray-50 text-gray-400 border-gray-200"
                          : !isSupported
                            ? "bg-gray-100 text-gray-300 border-gray-200 cursor-not-allowed"
                            : isSelected
                              ? "bg-[#FD5901] text-white border-[#FD5901]"
                              : "bg-white text-gray-700 border-gray-300 hover:border-orange-400"
                      }`}
                    >
                      {label}
                      {selectedPetType && !isSupported && (
                        <span className="absolute inset-0 flex items-center justify-center opacity-20">
                          <div className="w-full h-[1px] bg-black rotate-12"></div>
                        </span>
                      )}
                    </button>
                    {selectedPetType && !isSupported && (
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block w-32 bg-gray-800 text-white text-[10px] p-2 rounded text-center z-50">
                        Not available for {label} pets.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4"
        style={{ zIndex: 100 }}
      >
        <div className="bg-gray-50 w-full max-w-3xl max-h-[90vh] shadow-2xl overflow-y-auto transform transition-all duration-300 rounded-2xl responsive-animation">
          {/* Header */}
          <div className="px-8 sm:px-6 sm:pt-2 text-black relative">
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 p-2 text-gray-600 hover:bg-gray-200 rounded-full"
            >
              <FaTimes className="w-5 h-5" />
            </button>
            <h5 className="text-xl mt-2 text-[#000048] font-bold">
              Choose your room
            </h5>
          </div>

          {/* Booking Summary */}
          <div className="p-4 sm:p-2 space-y-1 my-[-14px]">
            {/* Booking Summary Card */}
            <div className="relative bg-gradient-to-br from-orange-100 via-amber-50 to-yellow-100 p-1 sm:mt-6 rounded-xl border border-orange-200 shadow-sm hover:shadow-md transition-all duration-300">
              <div className="absolute top-3 right-3 text-orange-500 text-xl">
                📅
              </div>
              <h5 className="text-lg px-4 mb-2 sm:text-lg font-bold text-[#000048] sm:mb-3">
                Booking Summary
              </h5>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-center ">
                {/* Check-in */}
                <div className="bg-white/70 backdrop-blur-sm p-1 mt-2 rounded-xl shadow-sm hover:shadow-md transition-all">
                  <p className="text-[#3B82F6] text-base font-medium">
                    Check-in
                  </p>
                  <p className="font-semibold text-sm text-[#FF6216] mt-2">
                    {formatDate?.(selectedStartDate) || "Not selected"}
                  </p>
                </div>

                {/* Check-out */}
                <div className="bg-white/70 backdrop-blur-sm p-1 mt-2 rounded-xl shadow-sm hover:shadow-md transition-all">
                  <p className="text-[#3B82F6] text-base font-medium">
                    Check-out
                  </p>
                  <p className="font-semibold text-sm text-[#FF6216] mt-2">
                    {formatDate?.(selectedEndDate) || "Not selected"}
                  </p>
                </div>

                {/* Duration */}
                <div className="bg-white/70 backdrop-blur-sm p-1 mt-2 rounded-xl shadow-sm hover:shadow-md transition-all">
                  <p className="text-[#3B82F6] text-base font-medium">
                    Duration
                  </p>
                  <p className="font-semibold text-lg text-[#FF6216] mt-1">
                    {calculateDays?.() || 0}{" "}
                    <span className="text-sm text-[#FF6216] mt-2 ">days</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Pet Summary Section */}
            {renderPetSection()}
          </div>

          {/* Room Carousel */}
          <div className="space-y-4 px-3 sm:px-2 pb-4">
            <div className="relative">
              <button
                onClick={handlePrevRoom}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-2 bg-white shadow-xl hover:bg-gray-100 rounded-full z-20 transition-all duration-200"
                disabled={
                  isAnimating || rooms.length <= 2 || currentRoomIndex === 0
                }
              >
                <FaChevronLeft className="w-5 h-5 text-gray-700" />
              </button>

              <button
                onClick={handleNextRoom}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-2 bg-white shadow-xl hover:bg-gray-100 rounded-full z-20 transition-all duration-200"
                disabled={
                  isAnimating ||
                  rooms.length <= 1 ||
                  currentRoomIndex >= rooms.length - 1
                }
              >
                <FaChevronRight className="w-5 h-5 text-gray-700" />
              </button>

              {/* --- ROOMS VIEWPORT (HIDES EXTRA CARDS) --- */}
              <div className="overflow-hidden">
                <div
                  style={{
                    transform: `translateX(-${
                      currentRoomIndex * getStepPercentage()
                    }%)`,
                  }}
                  className="flex transition-transform duration-500 ease-in-out will-change-transform"
                >
                  {isLoading ? (
                    <div className="w-full text-center py-12 text-gray-500 font-medium">
                      Loading available rooms...
                    </div>
                  ) : rooms.length === 0 ? (
                    <div className="w-full flex flex-col items-center justify-center py-10 px-4 text-center">
                      <div className="bg-orange-50 p-4 rounded-full mb-3">
                        <span className="text-xl">🏨</span>
                      </div>
                      <p className="text-gray-600 font-medium max-w-xs mx-auto">
                        {!selectedPetId && !selectedPetType
                          ? "Please select your pet above to view available rooms."
                          : "We couldn't find any available rooms for the selected pet and dates."}
                      </p>
                      {!selectedPetId && !selectedPetType && (
                        <span className="text-xs text-orange-500 mt-2 font-semibold uppercase tracking-wider">
                          Selection Required
                        </span>
                      )}
                    </div>
                  ) : (
                    rooms.map((room, index) => {
                      const priceByRoom = room.price ?? 0; // Use room.price from mapRoomData

                      const isSelected =
                        selectedRoom?.roomTypeId === room.roomTypeId;
                      const hasImage = room.images?.length > 0;
                      // const priceByRoom =
                      //pendingService?.price_by_room_type?.[index]?.price ?? 0;
                      // const isSelected =
                      // selectedRoom?.roomTypeId === room.roomTypeId;

                      return (
                        <div
                          key={room.roomTypeId}
                          onClick={() => {
                            // 🟢 LOGGED-IN USER FLOW
                            if (isUserLoggedIn && !selectedPetId) {
                              toast.info("Please select a pet first");
                              return;
                            }

                            // 🟡 GUEST USER FLOW
                            if (!isUserLoggedIn && !selectedPetSize) {
                              toast.info("Please select pet size first");
                              return;
                            }

                            //setRoomSelectionError("Please select pet size first");
                            setRoomSelectionError("");
                            if (selectedRoom?.roomTypeId === room.roomTypeId) {
                              // 🔴 DESELECT
                              setSelectedRoom(null);
                              fetchBoardingPrice({ includeRoom: false });
                            } else {
                              // 🟢 SELECT
                              setSelectedRoom(room);
                            }
                          }}
                          // **CORRECTION:** Make width 100% on mobile, 50% on desktop (2 cards visible)
                          className="flex-shrink-0 w-full sm:w-1/2 md:w-1/3 px-1 mt-4"
                        >
                          {/* lg classes removed to ensure consistent layout */}
                          <div
                            className={`group relative border-2 cursor-pointer rounded-2xl overflow-hidden transition-all duration-300 h-full ${
                              isSelected
                                ? "border-green-500 shadow-xl scale-[1.01]"
                                : "border-gray-200 hover:shadow-lg hover:border-orange-400"
                            }`}
                          >
                            {/* Image (Decreased Height to h-36 for shorter card) */}
                            {/* 1. CONDITIONAL IMAGE SECTION: Only renders if room.image exists */}
                            {hasImage && (
                              <div className="relative h-24 sm:h-28 overflow-hidden border-b border-gray-100">
                                <img
                                  src={`${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL}/${room.images[0]}`}
                                  // alt={room.room_name || room.name || "Room"}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                  // Optional: silent failure handling (no visible change)
                                />
                                {isSelected && (
                                  <div className="absolute inset-0 bg-green-600 bg-opacity-20 flex items-center justify-center">
                                    <span className="bg-green-600 text-white text-xs px-3 py-1 rounded-full font-semibold">
                                      Selected
                                    </span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Content (Decreased padding to p-3, decreased spacing to space-y-2) */}
                            <div className="p-2.5 space-y-1 sm:p-2 sm:space-y-1">
                              <h5 className="font-semibold text-sm text-gray-800 px-2 flex justify-between items-center">
                                <span>{room.name}</span>
                                { <span className="text-orange-600 font-bold text-sm">
                                  ₹{priceByRoom} / day
                                </span> }
                              </h5>
                              {/* 3. SHOW SELECTED BADGE HERE IF IMAGE IS MISSING */}
                              {!hasImage && isSelected && (
                                <div className="flex items-top justify-center ">
                                  <span className="bg-green-600 text-white text-[11px] px-3 py-0.5 rounded-full font-semibold">
                                    Selected
                                  </span>
                                </div>
                              )}

                              {/* Room Details List - Improved alignment using grid */}
                              <dl className="mt-2 space-y-1.5 text-xs text-gray-700">
                                {room.details?.Environment && (
                                  <div className="flex items-start gap-2 px-2">
                                    <span className="text-gray-400">🌿</span>
                                    <div>
                                      <span className="font-medium text-gray-500">
                                        Environment
                                      </span>
                                      <span className="mx-1">•</span>
                                      <span className="font-semibold text-gray-800">
                                        {room.details.Environment}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {room.details?.Category && (
                                  <div className="flex items-start gap-2 px-2">
                                    <span className="text-gray-400">🏷️</span>
                                    <div>
                                      <span className="font-medium text-gray-500">
                                        Room Category
                                      </span>
                                      <span className="mx-1">•</span>
                                      <span className="font-semibold text-gray-800">
                                        {room.details.Category}
                                      </span>
                                    </div>
                                  </div>
                                )}

                                {room.details?.Camera && (
                                  <div className="flex items-start gap-2 px-2">
                                    <span className="text-gray-400">📹</span>
                                    <div>
                                      <span className="font-medium text-gray-500">
                                        Camera
                                      </span>
                                      <span className="mx-1">•</span>
                                      <span className="font-semibold text-gray-800">
                                        {room.details.Camera}
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </dl>
                            </div>

                            {/* Hover overlay accent */}
                            <div className="absolute inset-0 opacity-0 group-hover:opacity-10 group-hover:bg-orange-400 transition-all duration-300 rounded-2xl" />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                {/* End of Rooms Track */}
              </div>
              {/* End of Rooms Viewport */}
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="flex gap-4 pt-4 pb-4 border-t border-gray-200 px-6">
            <button
              onClick={closeModal}
              className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg font-semibold text-gray-700"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmBooking}
              className="flex-1 py-2 px-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold disabled:opacity-50"
              disabled={isLoading || rooms.length === 0 || apiPriceLoading}
            >
              {apiPriceLoading
                ? "Calculating Price..."
                : `Confirm & Add to Cart ₹${displayedPrice}`}
            </button>
          </div>
        </div>

        <style jsx>{`
          @keyframes scale-in {
            from {
              opacity: 0;
              transform: scale(0.9);
            }
            to {
              opacity: 1;
              transform: scale(1);
            }
          }
          @keyframes slide-in-right {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
          .responsive-animation {
            animation: scale-in 0.3s ease-out;
          }
          .will-change-transform {
            will-change: transform;
          }
        `}</style>
      </div>
      {/* Pet Drawer */}
      {/* Pet Drawer */}
      <PetDrawer
        isOpen={isAddPetDrawerOpen}
        onClose={() => setIsAddPetDrawerOpen(false)}
        onSave={(newPet) => {
          if (newPet) {
            console.log("New pet added:", newPet);
            setPets((prev) => [
              ...prev,
              {
                ...newPet,
                _id: newPet._id || newPet.id,
                // Ensure pet_type is properly set
                pet_type: newPet.pet_type || newPet.petType,
              },
            ]);

            // IMPORTANT: Fetch vendor sizes for the new pet's type
            if (
              newPet.pet_type &&
              allowedPetTypeIds.includes(newPet.pet_type)
            ) {
              console.log("Fetching vendor sizes for newly added pet");
              fetchPetSizes(newPet.pet_type);
            }
          } else {
            // Refresh pets list
            fetchPets();
          }
          setIsAddPetDrawerOpen(false);
        }}
      />
    </>,
    document.body,
  );
};

export default BoardingCalendarModel;